import type {
  Account,
  AccountInput,
  AccountOption,
  AccountOptionsQuery,
  AccountRole,
  AccountRolesUpdate,
  AccountUpdate,
  FiscalYear,
  FiscalYearInput,
  JournalEntry,
  JournalEntrySummary,
  JournalListSummary,
  JournalSummaryQuery,
  ManualJournalInput,
  Page,
  ReverseJournalInput,
  TrialBalance,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { JournalListParams } from './search'

export const ledgerKeys = {
  accounts: ['accounts'] as const,
  accountRoles: ['account-roles'] as const,
  fiscalYears: ['fiscal-years'] as const,
  journals: ['journal-entries'] as const,
  journalList: (query: JournalListParams) => [...ledgerKeys.journals, 'list', query] as const,
  journalSummary: (query: JournalSummaryQuery) => [...ledgerKeys.journals, 'summary', query] as const,
  journal: (id: string) => [...ledgerKeys.journals, 'detail', id] as const,
  reports: ['reports'] as const,
  trialBalance: (from: string, to: string) => [...ledgerKeys.reports, 'trial-balance', from, to] as const,
}

/** Balances of cash and bank accounts are ledger balances: they move with every posting. */
const TREASURY_KEY = ['treasury-accounts'] as const

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

export const accountsQueryOptions = queryOptions({
  queryKey: ledgerKeys.accounts,
  queryFn: ({ signal }) => api.get<Account[]>('/accounts', { signal }),
  staleTime: 60_000,
})

/**
 * `GET /accounts/options?purpose=`: the accounts a purchase line (`purchase`) or an expense and its
 * category (`expense`) may be charged to, filtered by the API with the rule its validation applies.
 * Callable without ledger.read by those who create purchases or expenses. Under the accounts key, so
 * a change to the chart or its roles refreshes it.
 */
export function accountOptionsQueryOptions(purpose: AccountOptionsQuery['purpose']) {
  return queryOptions({
    queryKey: [...ledgerKeys.accounts, 'options', purpose] as const,
    queryFn: ({ signal }) => api.get<AccountOption[]>('/accounts/options', { signal, query: { purpose } }),
    staleTime: 60_000,
  })
}

export const accountRolesQueryOptions = queryOptions({
  queryKey: ledgerKeys.accountRoles,
  queryFn: ({ signal }) =>
    api.get<{ role: AccountRole; accountId: string | null }[]>('/account-roles', { signal }),
})

export const fiscalYearsQueryOptions = queryOptions({
  queryKey: ledgerKeys.fiscalYears,
  queryFn: ({ signal }) => api.get<FiscalYear[]>('/fiscal-years', { signal }),
  staleTime: 60_000,
})

export function journalListQueryOptions(query: JournalListParams) {
  return queryOptions({
    queryKey: ledgerKeys.journalList(query),
    queryFn: ({ signal }) =>
      api.get<Page<JournalEntrySummary>>('/journal-entries', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          from: query.from,
          to: query.to,
          kind: query.kind,
          accountId: query.accountId,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Entries and totals per kind, and the monthly trend, for the list's filters other than the kind. */
export function journalSummaryQueryOptions(list: JournalListParams) {
  const query: JournalSummaryQuery = { from: list.from, to: list.to, accountId: list.accountId }
  return queryOptions({
    queryKey: ledgerKeys.journalSummary(query),
    queryFn: ({ signal }) =>
      api.get<JournalListSummary>('/journal-entries/summary', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

export function journalQueryOptions(id: string) {
  return queryOptions({
    queryKey: ledgerKeys.journal(id),
    queryFn: ({ signal }) => api.get<JournalEntry>(path('/journal-entries', id), { signal }),
    staleTime: 60_000,
  })
}

export function trialBalanceQueryOptions(from: string, to: string) {
  return queryOptions({
    queryKey: ledgerKeys.trialBalance(from, to),
    queryFn: ({ signal }) => api.get<TrialBalance>('/reports/trial-balance', { signal, query: { from, to } }),
    placeholderData: keepPreviousData,
  })
}

/** Anything that posts to the ledger changes entries, reports and cash balances. */
export function invalidateLedger(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ledgerKeys.journals }),
    queryClient.invalidateQueries({ queryKey: ledgerKeys.reports }),
    queryClient.invalidateQueries({ queryKey: TREASURY_KEY }),
  ])
}

export function useSaveAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: AccountInput | AccountUpdate }) =>
      id ? api.put<Account>(path('/accounts', id), input) : api.post<Account>('/accounts', input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ledgerKeys.accounts }),
        queryClient.invalidateQueries({ queryKey: ledgerKeys.reports }),
      ]),
  })
}

export function useSetAccountArchived() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.post<void>(path('/accounts', id, archived ? 'archive' : 'restore')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ledgerKeys.accounts }),
  })
}

export function useSaveAccountRoles() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AccountRolesUpdate) => api.put<void>('/account-roles', input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ledgerKeys.accountRoles }),
        // Role badges on the chart of accounts.
        queryClient.invalidateQueries({ queryKey: ledgerKeys.accounts }),
      ]),
  })
}

export function useCreateFiscalYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: FiscalYearInput) => api.post<FiscalYear>('/fiscal-years', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ledgerKeys.fiscalYears }),
  })
}

export function usePostJournal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ManualJournalInput) => api.post<JournalEntry>('/journal-entries', input),
    onSuccess: (entry) => {
      queryClient.setQueryData(ledgerKeys.journal(entry.id), entry)
      return invalidateLedger(queryClient)
    },
  })
}

export function useReverseJournal(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ReverseJournalInput) =>
      api.post<JournalEntry>(path('/journal-entries', id, 'reverse'), input),
    onSuccess: (reversal) => {
      queryClient.setQueryData(ledgerKeys.journal(reversal.id), reversal)
      return invalidateLedger(queryClient)
    },
  })
}
